import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'src/store/slices/auth/authSelectors';
import { ContactsState, Registrant, REGISTRANT_OPTIONS } from 'src/types/flow';
import { Person } from 'src/types/person';

export const useUserRole = (
  registrant?: Registrant,
  contacts?: ContactsState,
  tenant?: Person,
  compareWith?: Person
) => {
  const selectedLoggedInUserDetails = useSelector(selectCurrentUser);
  let userEmail = selectedLoggedInUserDetails?.email;
  if (compareWith) {
    userEmail = compareWith.email;
  }
  const isOwner = useMemo(
    () =>
      userEmail &&
      registrant?.owner?.some((owner) => owner.email === userEmail),
    [registrant, userEmail]
  );

  const ownerIndex = useMemo((): number => {
    if (isOwner) {
      return (
        (userEmail &&
          registrant?.owner?.findIndex((owner) => owner.email === userEmail)) ??
        -1
      );
    }
    return -1;
  }, [registrant, userEmail, isOwner]);

  const isPropertyManager = useMemo(() => {
    if (contacts) {
      return userEmail && contacts?.propertyManager?.email === userEmail;
    }

    return (
      registrant?.registrantType ===
        REGISTRANT_OPTIONS.PROPERTY_MANAGER_INDEPENDENT ||
      registrant?.registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER
    );
  }, [contacts, registrant, userEmail]);

  const isTenant = useMemo(
    () => userEmail && tenant?.email === userEmail,
    [tenant, userEmail]
  );

  const isRegistrant = useMemo(() => {
    if (!registrant?.registrant?.email) return false;
    if (isOwner) {
      return registrant?.owner?.some(
        (owner) => owner.email === registrant.registrant?.email
      );
    } else if (isPropertyManager) {
      return registrant?.registrant?.email === contacts?.propertyManager?.email;
    }
    return false;
  }, [registrant, isOwner, isPropertyManager, contacts]);

  const selectedRegistrantOption = useMemo(() => {
    if (registrant?.registrantType === REGISTRANT_OPTIONS.TENANT) {
      if (isTenant) {
        return REGISTRANT_OPTIONS.TENANT;
      } else if (isPropertyManager) {
        return REGISTRANT_OPTIONS.PROPERTY_MANAGER;
      }

      return REGISTRANT_OPTIONS.OWNER;
    } else if (
      registrant?.registrantType === REGISTRANT_OPTIONS.PROPERTY_MANAGER
    ) {
      return isPropertyManager
        ? REGISTRANT_OPTIONS.PROPERTY_MANAGER
        : REGISTRANT_OPTIONS.OWNER;
    } else if (registrant?.registrantType === REGISTRANT_OPTIONS.OWNER) {
      return !isOwner
        ? REGISTRANT_OPTIONS.PROPERTY_MANAGER
        : REGISTRANT_OPTIONS.OWNER;
    }

    return registrant?.registrantType;
  }, [registrant, isPropertyManager, isOwner, isTenant]);

  const isRegistrantTenant = useMemo(() => {
    return registrant?.registrantType === REGISTRANT_OPTIONS.TENANT;
  }, [registrant]);

  return {
    isOwner,
    isRegistrantTenant,
    isPropertyManager,
    isTenant,
    isRegistrant,
    ownerIndex,
    selectedRegistrantOption,
    userEmail,
  };
};
